'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Smartphone, Globe, ArrowRight, Play, CheckCircle, Navigation, BarChart3, Car, X, ExternalLink, Apple, Zap,  } from 'lucide-react';

interface DemoItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: string;
  platform: string;
  features: string[];
  demoUrl: string;
  color: string;
  screenshots: { label: string; desc: string }[];
}

const demos: DemoItem[] = [
  {
    id: 'passenger-android',
    title: 'Passenger App',
    subtitle: 'Android PWA',
    description:
      'Experience the full passenger journey — search zones, pick vehicle type, get instant fare estimates, book a ride, and track your driver live.',
    icon: <Smartphone className="w-6 h-6" />,
    badge: 'Android',
    badgeColor: '#3ddc84',
    platform: 'Android',
    features: [
      'Zone & address search',
      'Vehicle type selection',
      'Real-time fare estimation',
      'Stripe payment checkout',
      'Live driver tracking',
      'Booking history',
    ],
    demoUrl: '/passenger-app',
    color: '#3ddc84',
    screenshots: [
      { label: 'Book a Ride', desc: 'Enter pickup & drop-off, choose vehicle' },
      { label: 'Fare Estimate', desc: 'Instant pricing before you confirm' },
      { label: 'Live Tracking', desc: 'Watch your driver arrive in real-time' },
    ],
  },
  {
    id: 'passenger-ios',
    title: 'Passenger App',
    subtitle: 'iOS PWA',
    description:
      'The same seamless passenger experience optimised for iPhone and iPad — install to home screen in one tap, no App Store needed.',
    icon: <Apple className="w-6 h-6" />,
    badge: 'iOS',
    badgeColor: '#007aff',
    platform: 'iOS',
    features: [
      'Add to Home Screen (PWA)',
      'Touch ID / Face ID ready',
      'Offline-capable booking',
      'Push notifications',
      'Apple Pay via Stripe',
      'Smooth iOS animations',
    ],
    demoUrl: '/passenger-app',
    color: '#007aff',
    screenshots: [
      { label: 'Home Screen Install', desc: 'One-tap install, no App Store' },
      { label: 'Booking Flow', desc: 'Native-feel iOS interface' },
      { label: 'Payment', desc: 'Apple Pay & card checkout' },
    ],
  },
  {
    id: 'driver-android',
    title: 'Driver App',
    subtitle: 'Android PWA',
    description:
      'Drivers go online, receive live job alerts, accept or reject assignments, navigate to passengers, and complete trips — all from their Android browser.',
    icon: <Car className="w-6 h-6" />,
    badge: 'Android',
    badgeColor: '#3ddc84',
    platform: 'Android',
    features: [
      'Online / offline toggle',
      'Live job notifications',
      'Accept / reject with timer',
      'Google Maps navigation',
      'Trip start & completion',
      'Daily earnings summary',
    ],
    demoUrl: '/driver-app',
    color: '#f59e0b',
    screenshots: [
      { label: 'Job Alert', desc: 'Instant notification with 60s accept timer' },
      { label: 'Navigation', desc: 'One-tap Google Maps directions' },
      { label: 'Earnings', desc: 'Daily & weekly earnings breakdown' },
    ],
  },
  {
    id: 'driver-ios',
    title: 'Driver App',
    subtitle: 'iOS PWA',
    description:
      'The full driver workflow on iPhone — install to home screen, stay online, get job pings, and navigate to passengers without leaving the app.',
    icon: <Navigation className="w-6 h-6" />,
    badge: 'iOS',
    badgeColor: '#007aff',
    platform: 'iOS',
    features: [
      'Add to Home Screen (PWA)',
      'Background location tracking',
      'Silent push job alerts',
      'Apple Maps / Google Maps',
      'Trip history & receipts',
      'Offline job queue',
    ],
    demoUrl: '/driver-app',
    color: '#8b5cf6',
    screenshots: [
      { label: 'iOS Install', desc: 'Add to Home Screen in Safari' },
      { label: 'Live Jobs', desc: 'Real-time job feed with accept timer' },
      { label: 'Trip History', desc: 'Full earnings & trip log' },
    ],
  },
  {
    id: 'back-office',
    title: 'Back Office',
    subtitle: 'Operator Dashboard',
    description:
      'The full operator command centre — manage bookings, drivers, zones, pricing, staff, and revenue reports all from one dashboard.',
    icon: <BarChart3 className="w-6 h-6" />,
    platform: 'Web',
    features: [
      'Live booking dispatch board',
      'Driver availability grid',
      'Zone & pricing management',
      'Revenue & MRR reports',
      'Staff & role management',
      'Branding & domain setup',
    ],
    demoUrl: '/company-admin-dashboard',
    color: '#f59e0b',
    screenshots: [
      { label: 'Dashboard', desc: 'KPIs, live bookings, driver status' },
      { label: 'Dispatch', desc: 'Assign and track rides in real-time' },
      { label: 'Reports', desc: 'Revenue, payouts, reconciliation' },
    ],
  },
  {
    id: 'website',
    title: 'Marketing Website',
    subtitle: 'Public Landing Page',
    description:
      'See the white-label marketing site your passengers land on — pricing, features, and a branded sign-up flow under your own domain.',
    icon: <Globe className="w-6 h-6" />,
    platform: 'Web',
    features: [
      'Fully white-labelled',
      'Custom domain & SSL',
      'Pricing plans display',
      'Passenger sign-up flow',
      'SEO-optimised pages',
      'Mobile responsive',
    ],
    demoUrl: '/landing',
    color: '#10b981',
    screenshots: [
      { label: 'Hero Section', desc: 'Your brand, your domain' },
      { label: 'Pricing', desc: 'Plans tailored to your fleet' },
      { label: 'Sign Up', desc: 'Passenger onboarding flow' },
    ],
  },
];

export default function DemoPage() {
  const [activeDemo, setActiveDemo] = useState<DemoItem | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* NAV */}
      <nav
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'rgba(10,10,10,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing" className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: 'var(--primary)', color: '#000' }}
            >
              R
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: 'var(--foreground)' }}>
              RideOS
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: 'Features', href: '/landing#features' },
              { label: 'Pricing', href: '/landing#pricing' },
              { label: 'Demo', href: '/demo' },
            ].map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="text-sm transition-colors"
                style={{ color: item.label === 'Demo' ? 'var(--primary)' : 'var(--muted-foreground)' }}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-sm px-4 py-2 rounded-lg" style={{ color: 'var(--muted-foreground)' }}>
              Sign in
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="text-sm px-4 py-2 rounded-lg font-semibold"
              style={{ background: 'var(--primary)', color: '#000' }}
            >
              Start free trial
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-32 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border"
            style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.25)', color: 'var(--primary)' }}
          >
            <Play className="w-3 h-3" />
            Interactive demos — no sign-up required
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5" style={{ color: 'var(--foreground)' }}>
            Try before you subscribe
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Explore every part of the RideOS platform — passenger app, driver app, back office, and website — exactly as your customers and drivers will experience it.
          </p>
        </div>
      </section>

      {/* DEMO GRID */}
      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section labels */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {demos.map((demo) => (
              <div
                key={demo.id}
                className="group relative p-6 rounded-2xl border flex flex-col cursor-pointer transition-all duration-200"
                style={{
                  background: hoveredId === demo.id ? `rgba(${hexToRgb(demo.color)}, 0.06)` : 'var(--card)',
                  borderColor: hoveredId === demo.id ? demo.color : 'var(--border)',
                  boxShadow: hoveredId === demo.id ? `0 0 30px rgba(${hexToRgb(demo.color)}, 0.12)` : 'none',
                }}
                onMouseEnter={() => setHoveredId(demo.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: `rgba(${hexToRgb(demo.color)}, 0.15)`, color: demo.color }}
                  >
                    {demo.icon}
                  </div>
                  {demo.badge && (
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: `rgba(${hexToRgb(demo.badgeColor || demo.color)}, 0.15)`, color: demo.badgeColor || demo.color }}
                    >
                      {demo.badge}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-base font-bold mb-0.5" style={{ color: 'var(--foreground)' }}>
                  {demo.title}
                </h3>
                <p className="text-xs font-medium mb-3" style={{ color: demo.color }}>
                  {demo.subtitle}
                </p>
                <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: 'var(--muted-foreground)' }}>
                  {demo.description}
                </p>

                {/* Features */}
                <ul className="space-y-1.5 mb-6">
                  {demo.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: demo.color }} />
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f}</span>
                    </li>
                  ))}
                  {demo.features.length > 4 && (
                    <li className="text-xs pl-5.5" style={{ color: 'var(--muted-foreground)', paddingLeft: '22px' }}>
                      +{demo.features.length - 4} more features
                    </li>
                  )}
                </ul>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveDemo(demo)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold border transition-all"
                    style={{ borderColor: demo.color, color: demo.color, background: 'transparent' }}
                  >
                    <Play className="w-3.5 h-3.5" />
                    Preview
                  </button>
                  <Link
                    href={demo.demoUrl}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
                    style={{ background: demo.color, color: demo.id === 'back-office' || demo.id === 'website' ? '#000' : '#000' }}
                  >
                    Launch Demo
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="p-10 rounded-3xl border relative overflow-hidden"
            style={{ background: 'var(--card)', borderColor: 'rgba(245,158,11,0.3)' }}
          >
            <div
              className="absolute inset-0 opacity-5 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, var(--primary) 0%, transparent 70%)' }}
            />
            <div className="relative">
              <Zap className="w-8 h-8 mx-auto mb-4" style={{ color: 'var(--primary)' }} />
              <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
                Ready to launch your own platform?
              </h2>
              <p className="text-sm mb-7" style={{ color: 'var(--muted-foreground)' }}>
                Everything you just explored — under your brand, on your domain, in 20 minutes. 14-day free trial, no credit card required.
              </p>
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
                style={{ background: 'var(--primary)', color: '#000' }}
              >
                Start your free trial
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs mt-4" style={{ color: 'var(--muted-foreground)' }}>
                Setup in 20 minutes · No credit card · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PREVIEW MODAL */}
      {activeDemo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
          onClick={() => setActiveDemo(null)}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl border p-8"
            style={{ background: 'var(--card)', borderColor: activeDemo.color }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveDemo(null)}
              className="absolute top-4 right-4 p-1.5 rounded-lg"
              style={{ color: 'var(--muted-foreground)', background: 'var(--border)' }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `rgba(${hexToRgb(activeDemo.color)}, 0.15)`, color: activeDemo.color }}
              >
                {activeDemo.icon}
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>{activeDemo.title}</h3>
                <p className="text-xs font-medium" style={{ color: activeDemo.color }}>{activeDemo.subtitle}</p>
              </div>
            </div>

            <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
              {activeDemo.description}
            </p>

            {/* Screenshots preview */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {activeDemo.screenshots.map((s) => (
                <div
                  key={s.label}
                  className="p-3 rounded-xl border text-center"
                  style={{ borderColor: 'var(--border)', background: 'var(--background)' }}
                >
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--foreground)' }}>{s.label}</p>
                  <p className="text-2xs leading-tight" style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>{s.desc}</p>
                </div>
              ))}
            </div>

            {/* All features */}
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--muted-foreground)' }}>
                What's included
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {activeDemo.features.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: activeDemo.color }} />
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link
              href={activeDemo.demoUrl}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm"
              style={{ background: activeDemo.color, color: '#000' }}
              onClick={() => setActiveDemo(null)}
            >
              <ExternalLink className="w-4 h-4" />
              Open Full Demo
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '245,158,11';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}
